import { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Label } from '../../../atoms/label/label';
import { PhoneInput } from '../../../atoms/phone-input/phone-input';
import { Text } from '../../../atoms/text/text';
import { createSubmitHandler } from '../handle-submit';

import { BaseCaptureFormProps, FormLayout, useCollectedErrors } from './verification.shared';

const InlineLinkButton = ({
  children,
  onClick,
}: {
  readonly children: React.ReactNode;
  readonly onClick: () => void;
}) => (
  <button className="text-blue-600 underline cursor-pointer" onClick={onClick} type="button">
    {children}
  </button>
);

const AlreadyHaveCodeHelpText = ({
  navigateToCode,
  t,
}: {
  readonly navigateToCode: () => void;
  readonly t: (key: string) => string;
}) => (
  <Text>
    <Trans
      components={{
        enterCodeLink: (
          <InlineLinkButton onClick={navigateToCode}>{t('page.mfaSetup.phone.enterCodeHere')}</InlineLinkButton>
        ),
      }}
      i18nKey="page.mfaSetup.phone.alreadyHaveCode"
    />
  </Text>
);

type PhoneCaptureFormProps = BaseCaptureFormProps & {
  readonly errorInvalid?: string;
  readonly navigateToCode?: () => void;
};

const PhoneField = ({
  autoComplete,
  control,
  errorMessages,
  errorRequired,
  inputId,
  placeholder,
  invalidMessage,
  isPhoneCountryValid,
  isPhoneNumberValid,
  setIsPhoneCountryValid,
  setIsPhoneNumberValid,
}: {
  readonly autoComplete?: string;
  readonly control: ReturnType<typeof useForm<{ value: string }>>['control'];
  readonly errorMessages: string[];
  readonly errorRequired: string;
  readonly inputId?: string;
  readonly placeholder?: string;
  readonly invalidMessage: string;
  readonly isPhoneCountryValid: boolean;
  readonly isPhoneNumberValid: boolean;
  readonly setIsPhoneCountryValid: (isValid: boolean) => void;
  readonly setIsPhoneNumberValid: (isValid: boolean) => void;
}) => (
  <Controller
    control={control}
    name="value"
    render={({ field: { name, onBlur, onChange, value } }) => (
      <PhoneInput
        autoComplete={autoComplete}
        id={inputId}
        name={name}
        onBlur={onBlur}
        onChangeNumber={onChange}
        placeholder={placeholder}
        setIsValidCountry={setIsPhoneCountryValid}
        setIsValidNumber={setIsPhoneNumberValid}
        value={value}
        variant={errorMessages.length > 0 ? 'error' : 'default'}
      />
    )}
    rules={{
      required: errorRequired,
      validate: (value: string) => {
        if (value?.trim().length && (!isPhoneNumberValid || !isPhoneCountryValid)) return invalidMessage;
        return true;
      },
    }}
  />
);

const usePhoneValidation = () => {
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);
  const [isPhoneCountryValid, setIsPhoneCountryValid] = useState(true);

  return {
    isPhoneCountryValid,
    isPhoneNumberValid,
    setIsPhoneCountryValid,
    setIsPhoneNumberValid,
  };
};

const usePhoneCaptureForm = (onSubmit: (value: string) => Promise<void> | void) => {
  const {
    clearErrors,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<{ value: string }>({ defaultValues: { value: '' } });

  const submitHandler = handleSubmit(
    createSubmitHandler({
      onSubmit: async ({ value }) => onSubmit(value.trim()),
      setError,
    })
  );

  return { clearErrors, control, errors, isSubmitting, setError, submitHandler };
};

const PhoneCaptureForm = ({
  autoComplete,
  buttonLabel,
  description,
  errorInvalid,
  errorRequired,
  inputLabel,
  navigateToCode,
  onSubmit,
  placeholder,
  title,
}: PhoneCaptureFormProps) => {
  const { t } = useTranslation();
  const invalidMessage = errorInvalid ?? t('auth.common.errors.phoneNumberInvalid');
  const { clearErrors, control, errors, isSubmitting, setError, submitHandler } = usePhoneCaptureForm(onSubmit);
  const { isPhoneCountryValid, isPhoneNumberValid, setIsPhoneCountryValid, setIsPhoneNumberValid } =
    usePhoneValidation();

  useEffect(() => {
    isPhoneCountryValid ? clearErrors('value') : setError('value', { message: invalidMessage });
  }, [clearErrors, invalidMessage, isPhoneCountryValid, setError]);

  const errorMessages = useCollectedErrors(errors);
  const id = useId();

  return (
    <FormLayout
      buttonLabel={buttonLabel}
      description={description}
      errorMessages={errorMessages}
      isSubmitting={isSubmitting}
      submitHandler={submitHandler}
      title={title}
    >
      {inputLabel ? <Label htmlFor={id}>{inputLabel}</Label> : null}
      <PhoneField
        autoComplete={autoComplete}
        control={control}
        errorMessages={errorMessages}
        errorRequired={errorRequired}
        inputId={id}
        invalidMessage={invalidMessage}
        isPhoneCountryValid={isPhoneCountryValid}
        isPhoneNumberValid={isPhoneNumberValid}
        placeholder={placeholder}
        setIsPhoneCountryValid={setIsPhoneCountryValid}
        setIsPhoneNumberValid={setIsPhoneNumberValid}
      />
      {navigateToCode ? <AlreadyHaveCodeHelpText navigateToCode={navigateToCode} t={t} /> : null}
    </FormLayout>
  );
};

export { PhoneCaptureForm };
